import { Component, DestroyRef, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../components/inputs/status-filter/status-filter.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { CampaignTableComponent } from '../../../../components/tables/campaign-table/campaign-table.component';
import { CampaignInsightsService } from '../../../../components/tables/campaign-table/campaign-insights.service';
import { CampaignsService, CampaignType } from '../../../../services/campaign/campaigns-api.service';
import { ApiService } from '../../../../services/api/api.service';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { FeedbackCardComponent } from '../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { PaginationComponent } from '../../../../components/inputs/pagination/pagination.component';
import { EmptyStateComponent } from '../../../../components/ui/empty-state/empty-state.component';
import { IPagination } from '../../../../types/contactModule';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { CampaignDuplicateService } from '../create-campaign/campaign-duplicate.service';
import { CAMPAIGN_STATUS_GROUPS, CampaignStatusGroup } from './campaign-facts';
import { formatCount } from '../../../../../utils/sms';

export type TCampaignSort =
  | 'name,asc'
  | 'name,desc'
  | 'createdAt,asc'
  | 'createdAt,desc'
  | 'modifiedAt,asc'
  | 'modifiedAt,desc';

/** A status group key from CAMPAIGN_STATUS_GROUPS ('running', 'completed'…). */
export type TCampaignStatus = string;

interface StatusCount {
  group: CampaignStatusGroup;
  count: number;
}

/** Plain words for a failed page load; the raw error (with its URL) goes to the console only. */
function loadErrorText(error: any): string {
  const status = Number(error?.status);
  if (status === 0) return 'Check your connection.';
  if (status >= 500) return 'The campaigns service had a problem.';
  return 'Something went wrong.';
}

const EMPTY_PAGE: IPagination = {
  totalElements: 0,
  pageSize: 10,
  totalPages: 1,
  pageNumber: 0,
  offSet: 0,
};

@Component({
  selector: 'app-campaigns-list',
  standalone: true,
  imports: [
    FormsModule,
    SearchInputComponent,
    StatusFilterComponent,
    SortDropdownComponent,
    CampaignTableComponent,
    PromptCardComponent,
    FeedbackCardComponent,
    PaginationComponent,
    EmptyStateComponent,
    HasPermissionDirective,
  ],
  templateUrl: './campaigns-list.component.html',
  styleUrl: './campaigns-list.component.css',
})
export class CampaignsListComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private api = inject(ApiService);
  private insights = inject(CampaignInsightsService);
  private duplicator = inject(CampaignDuplicateService);
  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  campaignsService = inject(CampaignsService);

  readonly formatCount = formatCount;

  campaigns: CampaignType[] = [];
  /** True until the first page arrives, and while a new page loads. */
  loading = true;
  /** Why the last page failed to load, if it did. */
  loadError = '';
  showDelete = false;
  campaignId!: number;
  successMessage: string = '';
  errorMessage: string = '';
  private feedbackTimeoutId: ReturnType<typeof setTimeout> | undefined;

  /** Header counts per status group; null until they load (or if they fail). */
  statusCounts: StatusCount[] | null = null;
  countsLoading = true;

  /** The campaign being copied into the wizard. */
  duplicatingId: number | string | null = null;

  campaignsPagination: IPagination = { ...EMPTY_PAGE };

  searchTerm: string = '';
  sortValue: TCampaignSort = 'createdAt,desc';
  statusValue: TCampaignStatus | null = null;

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'name',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  readonly statusOptions: StatusFilterOption[] = [
    { value: null, title: 'All statuses', icon: 'list', tone: 'neutral' },
    ...CAMPAIGN_STATUS_GROUPS.map((group) => ({
      value: group.key,
      title: group.title,
      icon: group.icon,
      tone: group.tone,
    })),
  ];

  /** Query for the v2 list: size, sort, page, name.contains, status.in. */
  params: Record<string, any> = { size: 10, sort: `createdAt,desc` };

  private load$ = new Subject<Record<string, any>>();
  private search$ = new Subject<void>();

  get deleteTarget(): CampaignType | undefined {
    return this.campaigns.find((campaign) => campaign.id === this.campaignId);
  }

  /** Total across every status, from the header counts. */
  get countTotal(): number {
    return (this.statusCounts ?? []).reduce((sum, item) => sum + item.count, 0);
  }

  /** Only the states that have campaigns in them. */
  get visibleCounts(): StatusCount[] {
    return (this.statusCounts ?? []).filter((item) => item.count > 0);
  }

  get isFiltered(): boolean {
    return !!this.searchTerm.trim() || !!this.statusValue;
  }

  get pageSummary(): string {
    const { totalElements } = this.campaignsPagination;
    if (!totalElements) return '';
    const noun = totalElements === 1 ? 'campaign' : 'campaigns';
    return this.isFiltered ? `${formatCount(totalElements)} matching ${noun}` : `${formatCount(totalElements)} ${noun}`;
  }

  // ── Search, filter, sort ──

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    if (term.trim().length < 1) {
      delete this.params['name.contains'];
    } else {
      this.params['name.contains'] = term.trim();
    }
    this.search$.next();
  }

  /** The search box waits for typing to pause; everything else loads at once. */
  searchCampaign() {
    this.campaignsPagination.pageNumber = 0;
    this.reload();
  }

  updateSortCriteria(criteria: TCampaignSort) {
    this.sortValue = criteria;
    this.params['sort'] = criteria;
    this.campaignsPagination.pageNumber = 0;
    this.reload();
  }

  updateStatusCriteria(criteria: TCampaignStatus | null) {
    this.applyStatus(criteria);
    this.campaignsPagination.pageNumber = 0;
    this.reload();
    // Keep the address in step, so the link and back button match the list.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { status: this.statusValue },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Sets the status group (or none) and its `status.in` param, without loading. */
  private applyStatus(criteria: TCampaignStatus | null) {
    const group = CAMPAIGN_STATUS_GROUPS.find((item) => item.key === criteria?.toLowerCase()) ?? null;
    this.statusValue = group?.key ?? null;
    delete this.params['status.equals'];
    if (group) {
      this.params['status.in'] = group.statuses;
    } else {
      delete this.params['status.in'];
    }
  }

  /** Header counts double as filters; pressing the active one clears it. */
  toggleStatus(key: string) {
    this.updateStatusCriteria(this.statusValue === key ? null : key);
  }

  clearFilters() {
    this.searchTerm = '';
    delete this.params['name.contains'];
    this.updateStatusCriteria(null);
  }

  navigateToCreate() {
    this.router.navigate(['organization/campaigns/create-campaign']);
  }

  // ── Delete ──

  showDeletePrompt(id: number) {
    this.campaignId = id;
    this.showDelete = true;
  }

  deleteCampaign() {
    const target = this.deleteTarget;
    this.campaignsService.deleteCampaign(this.campaignId).subscribe({
      next: () => {
        this.showDelete = false;
        this.insights.forget(target);
        this.flash('success', target?.name ? `Deleted “${target.name}”.` : 'Campaign deleted.');
        // The last row of a later page: step back so the page is not empty.
        if (this.campaigns.length === 1 && this.campaignsPagination.pageNumber > 0) {
          this.campaignsPagination.pageNumber -= 1;
        }
        // Reload this page with the current search, filter and sort.
        this.reload();
        this.loadCounts();
      },
      // The service shows why it failed; the dialog stays open to try again.
      error: () => {},
    });
  }

  // ── Duplicate ──

  /** Copies the campaign into the wizard, unsaved. Nothing is created until launch. */
  duplicateCampaign(campaign: any) {
    if (this.duplicatingId !== null || !campaign?.id) return;
    this.duplicatingId = campaign.id;
    this.duplicator
      .prefill(campaign)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.duplicatingId = null;
          this.router.navigate(['/organization/campaigns/create-campaign'], {
            queryParams: { step: 'campaign-details' },
          });
        },
        error: () => {
          this.duplicatingId = null;
          this.flash(
            'error',
            `Couldn’t copy “${campaign.name ?? 'this campaign'}”. Check your connection and try again.`
          );
        },
      });
  }

  // ── Paging ──

  get updatedParams() {
    return { ...this.params, page: this.campaignsPagination.pageNumber };
  }

  updateCampaignsPage(newPage: number) {
    this.campaignsPagination.pageNumber = newPage;
    this.reload();
  }

  goToNextPage() {
    this.campaignsPagination.pageNumber += 1;
    this.reload();
  }

  goToPreviousPage() {
    this.campaignsPagination.pageNumber -= 1;
    this.reload();
  }

  updateCampaignsPageSize(newSize: number) {
    this.params['size'] = newSize;
    this.campaignsPagination.pageSize = newSize;
    this.campaignsPagination.pageNumber = 0;
    this.reload();
  }

  reload() {
    this.load$.next(this.updatedParams);
  }

  // ── Loading ──

  ngOnInit() {
    // The latest request wins, so a slow earlier search never overwrites a newer one.
    this.load$
      .pipe(
        tap(() => {
          this.loading = true;
          this.loadError = '';
        }),
        switchMap((params) =>
          this.api
            .get<any>(
              `blackkonnectcxpengagement/api/engagement-campaigns/v2/organisations/${this.campaignsService.organizationId}`,
              params
            )
            .pipe(
              map((data) => ({ data, error: '' })),
              catchError((error) => {
                console.error('Campaigns did not load', error);
                return of({ data: null, error: loadErrorText(error) });
              })
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ data, error }) => {
        this.loading = false;
        if (error) {
          this.loadError = error;
          return;
        }
        this.campaigns = Array.isArray(data?.items) ? data.items : [];
        const page = data?.itemPagination as IPagination | undefined;
        this.campaignsPagination = page ? { ...EMPTY_PAGE, ...page } : { ...EMPTY_PAGE, pageSize: this.params['size'] };
      });

    this.search$
      .pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.searchCampaign());

    // The dashboard (or a shared link) can open the list on one state. The
    // component is reused when only the query changes (e.g. the sidebar link
    // drops ?status), so follow every change, not just the first.
    let first = true;
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((query) => {
      const fromUrl = query.get('status');
      const key = CAMPAIGN_STATUS_GROUPS.find((item) => item.key === fromUrl?.toLowerCase())?.key ?? null;
      if (first) {
        first = false;
        this.applyStatus(key);
        return;
      }
      if (key === this.statusValue) return;
      this.applyStatus(key);
      this.campaignsPagination.pageNumber = 0;
      this.reload();
    });

    if (this.isBrowser) {
      this.reload();
      this.loadCounts();
    }
  }

  /** One cheap request per status group (size=1), read from the page totals. */
  loadCounts() {
    if (!this.isBrowser) return;
    this.countsLoading = true;
    const url = `blackkonnectcxpengagement/api/engagement-campaigns/v2/organisations/${this.campaignsService.organizationId}`;
    forkJoin(
      CAMPAIGN_STATUS_GROUPS.map((group) =>
        this.api.get<any>(url, { size: 1, page: 0, 'status.in': group.statuses }).pipe(
          map((data) => ({ group, count: Number(data?.itemPagination?.totalElements) || 0 })),
          catchError(() => of(null))
        )
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((counts) => {
        this.countsLoading = false;
        // Partial answers would mislead; show none rather than some.
        this.statusCounts = counts.every((item) => item !== null) ? (counts as StatusCount[]) : null;
      });
  }

  private flash(kind: 'success' | 'error', message: string) {
    if (this.feedbackTimeoutId) clearTimeout(this.feedbackTimeoutId);
    this.successMessage = kind === 'success' ? message : '';
    this.errorMessage = kind === 'error' ? message : '';
    this.feedbackTimeoutId = setTimeout(() => {
      this.successMessage = '';
      this.errorMessage = '';
      this.feedbackTimeoutId = undefined;
    }, kind === 'error' ? 6000 : 3000);
  }

  ngOnDestroy() {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
  }
}
